import React, { useState } from 'react';
import LightBox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import { withStyles } from '@material-ui/core/styles';
import ImageMetaData from './ImageMetaData';
import { ImageListProps } from '../../types';
import { popupStyles } from '../styles/popupStyles';
import { useBlobImages } from '../hooks/useBlobImages';
import { MESSAGES } from '../constants';

const styles = (theme: any) => ({
  container: popupStyles(theme).imageGridContainer,
  notAvailable: popupStyles(theme).imageListNotAvailable,
  image: popupStyles(theme).imageListItem,
  containerStart: popupStyles(theme).imageListContainerStart,
  containerCenter: popupStyles(theme).imageListContainerCenter,
  center: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: {
    color: theme.error,
  },
});

interface LightBoxState {
  startIndex: number;
}

const ImageList: React.FC<ImageListProps & { classes: any }> = ({
  images,
  label,
  imageURLDetailsByTimestamp,
  classes,
}) => {
  const [lightBox, setLightBox] = useState<LightBoxState | null>(null);
  const { loadedImages: blobImageUrls, fetchError } = useBlobImages(images);

  const showLightBox = (startIndex: number) => {
    setLightBox({ startIndex });
  };

  const hideLightBox = () => {
    setLightBox(null);
  };

  const getImageList = () => {
    const { extractTimeStampLatLongValuesFromURL } = require('../utils/imageUtils');
    return images.map((imgData, index) => {
      // Extract timestamp from URL to get metadata
      const { timestamp } = extractTimeStampLatLongValuesFromURL(imgData.url);
      const metadata = timestamp && imageURLDetailsByTimestamp ? imageURLDetailsByTimestamp[timestamp] ?? {} : {};
      // Use fetched blob data URL if available, otherwise use original URL
      const imageUrl = blobImageUrls[imgData.id] || imgData.url;

      return {
        key: `${imgData.id || timestamp || 'img'}-${index}`,
        url: imageUrl,
        title: `${label} (${index + 1})`,
        timestamp,
        ...metadata,
      };
    });
  };

  const imageList = getImageList();
  const imagesForLightBox = imageList.length > 1 ? imageList : undefined;
  const singleImageInfo = imageList.length === 1 ? imageList[0].url : undefined;
  const singleImageTitle = imageList.length === 1 ? imageList[0].title : undefined;

  return (
    <div
      className={`${classes.container} ${
        imageList?.length && !fetchError ? classes.containerStart : classes.containerCenter
      }`}
    >
      {fetchError ? (
        <div className={`${classes.center} ${classes.error}`}>
          {MESSAGES.ERROR_FETCHING_IMAGES}
        </div>
      ) : imageList.length ? (
        imageList.map((image, index) => (
          <ImageMetaData
            key={image.key}
            date={image.date}
            time={image.time}
            address={image.address}
          >
            <img
              src={image.url}
              alt={image.title}
              className={classes.image}
              onClick={() => showLightBox(index)}
            />
          </ImageMetaData>
        ))
      ) : (
        <div className={classes.notAvailable}>{MESSAGES.NOT_AVAILABLE}</div>
      )}
      {lightBox && (
        <LightBox
          images={imagesForLightBox}
          image={singleImageInfo}
          title={singleImageTitle}
          startIndex={lightBox.startIndex}
          onClose={hideLightBox}
        />
      )}
    </div>
  );
};

export default withStyles(styles)(ImageList);
